import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { FlashcardFolder } from '../../types/flashcard.types';
import { X } from 'lucide-react';
import { flashcardActions } from '../../redux/actions/flashcard.actions';

interface RootState {
  flashcard: {
    loading: boolean;
  };
}

interface FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  folder?: any | null; // Accept any folder object
}

const COLORS = [
  '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#F59E0B',
  '#10B981', '#14B8A6', '#06B6D4', '#6366F1', '#A855F7',
];

const ICONS = ['📁', '📚', '📖', '🎯', '🌟', '💼', '🎨', '🔬', '🎵', '⚡'];

export const FolderModal: React.FC<FolderModalProps> = ({ isOpen, onClose, onSuccess, folder }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state: RootState) => state.flashcard);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [icon, setIcon] = useState(ICONS[0]);
  const [visibility, setVisibility] = useState<'public' | 'private' | 'class'>('private');

  useEffect(() => {
    if (folder) {
      setName(folder.name);
      setDescription(folder.description || '');
      setColor(folder.color);
      setIcon(folder.icon);
      setVisibility(folder.visibility);
    } else {
      setName('');
      setDescription('');
      setColor(COLORS[0]);
      setIcon(ICONS[0]);
      setVisibility('private');
    }
  }, [folder, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      alert('Vui lòng nhập tên folder!');
      return;
    }

    const data = {
      name: name.trim(),
      description: description.trim() || undefined,
      color,
      icon,
      visibility,
    };

    try {
      if (folder) {
        await dispatch(flashcardActions.updateFolder(folder.id, data) as any);
      } else {
        await dispatch(flashcardActions.createFolder(data) as any);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving folder:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{folder ? 'Chỉnh sửa Folder' : 'Tạo Folder Mới'}</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Tên Folder *</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="VD: IELTS Vocabulary"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Mô tả</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Mô tả ngắn về folder này..."
              rows={3}
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Chọn màu</Label>
              <ColorGrid>
                {COLORS.map((c) => (
                  <ColorOption
                    key={c}
                    type="button"
                    color={c}
                    selected={c === color}
                    onClick={() => setColor(c)}
                  />
                ))}
              </ColorGrid>
            </FormGroup>

            <FormGroup>
              <Label>Chọn icon</Label>
              <IconGrid>
                {ICONS.map((i) => (
                  <IconOption
                    key={i}
                    type="button"
                    selected={i === icon}
                    onClick={() => setIcon(i)}
                  >
                    {i}
                  </IconOption>
                ))}
              </IconGrid>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Quyền truy cập</Label>
            <RadioGroup>
              <RadioOption>
                <input
                  type="radio"
                  id="private"
                  checked={visibility === 'private'}
                  onChange={() => setVisibility('private')}
                />
                <label htmlFor="private">
                  <strong>Riêng tư</strong> - Chỉ bạn xem được
                </label>
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  id="class"
                  checked={visibility === 'class'}
                  onChange={() => setVisibility('class')}
                />
                <label htmlFor="class">
                  <strong>Lớp học</strong> - Học sinh trong lớp xem được
                </label>
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  id="public"
                  checked={visibility === 'public'}
                  onChange={() => setVisibility('public')}
                />
                <label htmlFor="public">
                  <strong>Công khai</strong> - Ai cũng xem được
                </label>
              </RadioOption>
            </RadioGroup>
          </FormGroup>

          <ButtonGroup>
            <CancelButton type="button" onClick={onClose}>
              Hủy
            </CancelButton>
            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Đang lưu...' : folder ? 'Cập nhật' : 'Tạo Folder'}
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </Modal>
    </Overlay>
  );
};

// ==================== Styled Components ====================

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #6b7280;
  transition: color 0.2s;

  &:hover {
    color: #1f2937;
  }
`;

const Form = styled.form`
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
`;

const ColorOption = styled.button<{ color: string; selected: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color};
  border: ${props => (props.selected ? '3px solid #1f2937' : 'none')};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const IconGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
`;

const IconOption = styled.button<{ selected: boolean }>`
  padding: 8px;
  border: 2px solid ${props => (props.selected ? '#3b82f6' : '#d1d5db')};
  border-radius: 8px;
  font-size: 20px;
  cursor: pointer;
  background: ${props => (props.selected ? '#eff6ff' : 'white')};
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  input {
    margin-top: 4px;
    cursor: pointer;
  }

  label {
    cursor: pointer;
    font-size: 0.875rem;
    color: #4a5568;
    
    strong {
      color: #2d3748;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: #f3f4f6;
  color: #4b5563;

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

const SubmitButton = styled(Button)`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
`;
